import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Instagram } from "lucide-react";
import { MaskLines, Overline, Reveal } from "./Primitives";
import { useLang, useT } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, id, textarea, ...rest }) => (
  <label className="block group">
    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400 group-focus-within:text-[var(--gold)] transition-colors">{label}</span>
    {textarea
      ? <textarea id={id} data-testid={`contact-${id}-input`} rows={4} className="field resize-none" {...rest} />
      : <input id={id} data-testid={`contact-${id}-input`} className="field" {...rest} />}
  </label>
);

export const Contact = ({ service, setService }) => {
  const t = useT().contact;
  const { lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || !form.email.includes("@") || form.message.trim().length < 10) {
      toast.error(t.invalid);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, service, lang });
      if (typeof window.gtag === "function") window.gtag("event", "generate_lead", { form: "contact", service });
      navigate("/grazie");
    } catch {
      toast.error(t.err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-36 bg-[var(--bg2)] relative overflow-hidden">
      <div className="hero-glow opacity-60" />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-6">
          <Overline>{t.over}</Overline>
          <h2 className="mt-8 font-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[1] tracking-tight" data-testid="contact-title">
            <MaskLines lines={t.title} italicIndex={1} inView />
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-8 text-stone-300 text-base md:text-lg max-w-md">{t.sub}</p>
            <a href="mailto:lenemauro1990@gmail.com" data-testid="contact-email-link" className="mt-10 inline-block font-display text-2xl md:text-3xl link-underline break-all">
              lenemauro1990@gmail.com
            </a>
            <div className="mt-8">
              <a href="https://www.instagram.com/lenemauro/" target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link" className="btn-ghost">
                <Instagram size={18} className="text-[var(--gold)]" /> @lenemauro
              </a>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="lg:col-span-6">
          <form onSubmit={submit} className="rounded-[28px] border border-[var(--line)] bg-[#0c0a09]/70 backdrop-blur-xl p-6 md:p-10 space-y-8" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-8">
              <Field id="name" label={t.name} value={form.name} onChange={set("name")} maxLength={80} />
              <Field id="email" label={t.email} type="email" value={form.email} onChange={set("email")} />
            </div>
            <div>
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">{t.service}</span>
              <div className="mt-3 flex flex-wrap gap-2">
                {Object.entries(t.opts).map(([k, v]) => (
                  <button type="button" key={k} data-testid={`contact-service-${k}`} onClick={() => setService(k)}
                    className={`px-4 py-2 rounded-full text-sm border transition-colors duration-300 ${service === k ? "bg-[var(--gold)] text-[#0c0a09] border-[var(--gold)]" : "border-[var(--line)] text-stone-300 hover:border-stone-400"}`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>
            <Field id="message" label={t.message} textarea value={form.message} onChange={set("message")} maxLength={2000} />
            <button type="submit" disabled={loading} data-testid="contact-submit-btn" className="btn-gold w-full justify-center disabled:opacity-60">
              {loading ? <><Loader2 size={18} className="animate-spin" /> {t.sending}</> : <>{t.send} <ArrowUpRight size={18} /></>}
            </button>
            <p className="text-xs text-stone-400" data-testid="contact-privacy-note">
              {lang === "it" ? "Inviando il modulo dichiari di aver letto la " : "By sending this form you confirm you have read the "}
              <Link to="/privacy" data-testid="contact-privacy-link" className="underline underline-offset-4 hover:text-[var(--gold)]">{lang === "it" ? "Privacy Policy" : "Privacy Policy"}</Link>.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
